// @ts-check
import React from 'react';
import { injectIntl } from 'react-intl';
import classNames from 'classnames';

import BusComponent from '../BusComponent';
import Tooltip from '../Tooltip';
import { isEqualJson, mapLocationAnalysisItemToVisualReport } from '../../helpers/Util';

/**
 * @typedef Props
 * @property {import('../../types').VisualReport} visualReport
 * @property {import('react-intl').intlShape} intl
 *
 * @typedef State
 * @property {boolean} saved
 * @property {import('../../types').VisualReport} visualReport
 * @property {import('../../types').VisualReport[]} param0.savedReports
 *
 * @extends {BusComponent<Props, State>}
 */
class VisualReportSave extends BusComponent {
    constructor(props, context) {
        super(props, context);
        /** @type {State} */
        this.state = {
            saved: false,
            visualReport: props.visualReport,
            savedReports: [],
        };
    }

    componentDidMount() {
        this.bindGluBusEvents({
            VISUAL_REPORT_SAVE_SUCCESS: this.onSaveSuccess,
            VISUAL_REPORT_UPDATE_SUCCESS: this.onUpdateSuccess,
            VISUAL_REPORT_DELETE_SUCCESS: this.onDeleteSuccess,
            LOCATION_ANALYSIS_ITEM_UPDATED: this.onReset,
            VISUAL_REPORTS: this.onRetrievedVisualReports,
        });
        this.emit('VISUAL_REPORTS_REQUEST');
    }

    componentWillUnmount() {
        this.unbindGluBusEvents();
    }

    onRetrievedVisualReports = payload => {
        const savedReports = payload.visualReports;
        this.setState({ savedReports }, () => {
            this.checkIfAlreadySaved(this.props.visualReport, savedReports);
        });
    };

    /**
     * @param {import('../../types').VisualReport} visualReport
     * @param {import('../../types').VisualReport[]} savedReports
     */
    checkIfAlreadySaved = (visualReport, savedReports) => {
        if (visualReport.id) {
            this.setState({ saved: true });
            return;
        }
        let saved = null;
        const selection = [...visualReport.selection].sort();
        if (visualReport.facilityId) {
            saved = savedReports.find(
                report =>
                    report.facilityId === visualReport.facilityId &&
                    isEqualJson([...report.selection].sort(), selection) &&
                    report.profile === visualReport.profile,
            );
        } else {
            saved = savedReports.find(
                report =>
                    isEqualJson(report.point, visualReport.point) &&
                    isEqualJson([...report.selection].sort(), selection) &&
                    report.profile === visualReport.profile,
            );
        }

        if (saved) {
            this.setState({
                saved: true,
                visualReport: { ...this.state.visualReport, id: saved.id },
            });
        } else {
            this.setState({ saved: false });
        }
    };

    onSaveSuccess = ({ newReport }) => {
        this.setState({ saved: true, visualReport: newReport });
    };

    onUpdateSuccess = ({ newTitle }) => {
        this.setState({
            saved: true,
            visualReport: { ...this.state.visualReport, title: newTitle },
        });
    };

    onDeleteSuccess = ({ reportId }) => {
        const { visualReport } = this.state;
        if (visualReport.id === reportId) {
            this.setState({ saved: false, visualReport: { ...visualReport, id: undefined } });
        }
    };

    onSave = () => {
        const event = this.state.saved
            ? 'UPDATE_VISUAL_REPORT_POPUP_REQUEST'
            : 'VISUAL_REPORT_SAVE_POPUP_REQUEST';
        this.emit(event, { visualReport: this.state.visualReport, areaSelectorEnabled: true });
    };

    onReset = ({ locationAnalysisItem }) => {
        const newVR = mapLocationAnalysisItemToVisualReport(locationAnalysisItem);
        const oldPoint = this.state.visualReport.point;
        const newPoint = newVR.point;
        // If the pin didn't move (same lat/lng), just merge in the new selection/profile
        if (
            oldPoint &&
            newPoint &&
            oldPoint.lat === newPoint.lat &&
            oldPoint.lng === newPoint.lng
        ) {
            this.setState(({ visualReport }) => ({
                visualReport: {
                    ...visualReport,
                    selection: newVR.selection,
                    profile: newVR.profile,
                },
            }));
        } else {
            // Location moved: reset saved, clear id, then re-check against savedReports
            this.setState({ visualReport: newVR, saved: false }, () =>
                this.checkIfAlreadySaved(newVR, this.state.savedReports),
            );
        }
    };

    render() {
        const { saved } = this.state;

        const classes = classNames(
            'material-symbols-filled btn-flat-icon btn-flat-icon--outlined btn-flat-icon--primary',
            saved ? 'btn-flat-icon--highlighted' : '',
        );
        const icon = saved ? 'where_to_vote' : 'add_location_alt';
        const tooltipMessageId = saved
            ? 'locationAnalysis.editMarket'
            : 'locationAnalysis.saveMarket';

        return (
            <Tooltip
                placement="bottom"
                mouseEnterDelay={0.5}
                mouseLeaveDelay={0}
                overlay={this.props.intl.formatMessage({
                    id: tooltipMessageId,
                })}
            >
                <button className={classes} onClick={this.onSave}>
                    {icon}
                </button>
            </Tooltip>
        );
    }
}

export default injectIntl(VisualReportSave);
